/* ──────────────────────────────────────────────────────────────────
 * NEW SITE NAV TREE (HOME-634)
 * 한 트리에 GNB / site map / footer 전부 정의. 메뉴 추가/삭제는 이 파일만 수정.
 *
 * label/description/title 값은 next-intl `Menu.*` 키.
 * sub 메뉴는 `groups[]` 로 묶음 — title 없는 단일 그룹은 flat 리스트로 렌더,
 * title 있는 다중 그룹은 카테고리 컬럼으로 렌더 (시안의 Products/Company).
 * groups 없는 메뉴는 메가메뉴 없이 단순 링크 (시안의 Patients).
 * ───────────────────────────────────────────────────────────────── */

import type { MessageKeys, Messages, NestedKeyOf } from 'use-intl/core';
import { routing } from '@/navigation';

// routing.locales 와 단일 출처 유지 — locale 추가 시 navigation.ts 한 곳만 수정.
export type NavLocale = (typeof routing.locales)[number];

// Menu 네임스페이스의 leaf string key만 허용 — intermediate object key 제외.
export type MenuMessageKey = MessageKeys<
  Messages['Menu'],
  NestedKeyOf<Messages['Menu']>
>;

export interface NavLink {
  label: MenuMessageKey; // i18n key (next-intl `Menu.*`)
  // 외부 사이트의 locale 분기가 필요하면 함수 형태로. 예: acmg.3billion.io 는 ko 에 `/ko` suffix.
  href: string | ((locale: NavLocale) => string);
  allowedLocales?: NavLocale[];
  target?: '_blank';
  rel?: string;
  emphasis?: boolean; // 강조 표시 (예: site map 의 Order 빨강).
  // gtag label override. 미지정 시 labelKey 의 마지막 segment 사용.
  // 예: 'etc.customer-support' 의 legacy gtag 값이 'support' 인 경우.
  gtagLabel?: string;
}

export interface NavGroup {
  title?: MenuMessageKey; // i18n key, 없으면 그룹 헤더 미노출
  items: NavLink[];
}

export interface NavItem extends NavLink {
  key: string;
  description?: MenuMessageKey; // i18n key, 메가메뉴 좌측 영역
  groups?: NavGroup[];
}

export type NavTree = NavItem[];

export const SITE_NAV: NavTree = [
  {
    key: 'patients',
    label: 'main.patients',
    href: '/patients',
  },
  {
    key: 'providers',
    label: 'main.providers',
    href: '/providers',
    description: 'providers.description',
    groups: [
      {
        items: [
          { label: 'providers.overview', href: '/providers' },
          { label: 'providers.gene-browser', href: '/gene-browser' },
          {
            label: 'providers.gene-coverage-browser',
            href: '/gene-coverage-browser',
          },
          {
            label: 'providers.secondary-finding-guide',
            // acmg.3billion.io 는 next-intl 과 동일하게 ko 에만 `/ko` prefix.
            href: locale =>
              locale === 'ko'
                ? 'https://acmg.3billion.io/ko'
                : 'https://acmg.3billion.io',
            target: '_blank',
            rel: 'noopener',
          },
        ],
      },
    ],
  },
  {
    key: 'partners',
    label: 'main.partners',
    href: '/pharma',
    description: 'partners.description',
    groups: [
      {
        items: [
          { label: 'partners.pharma', href: '/pharma' },
          {
            label: 'partners.collaboration',
            href: '/collaboration',
            allowedLocales: ['ko'],
          },
          {
            label: 'partners.grant',
            href: '/grant',
            allowedLocales: ['en'],
          },
        ],
      },
    ],
  },
  {
    key: 'products',
    label: 'main.product',
    href: '/product',
    description: 'product.description',
    groups: [
      {
        title: 'product.section.diagnostic',
        items: [
          { label: 'product.overview', href: '/product' },
          { label: 'product.genome', href: '/whole-genome-sequencing' },
          { label: 'product.exome', href: '/whole-exome-sequencing' },
          { label: 'product.variant', href: '/sanger-sequencing' },
          { label: 'product.interpreter', href: '/genomic-data-analysis' },
          { label: 'product.gebra', href: '/gebra' },
        ],
      },
      {
        title: 'product.section.screening',
        items: [{ label: 'product.neo', href: '/neo', allowedLocales: ['en'] }],
      },
      {
        title: 'product.section.guide',
        items: [
          { label: 'product.sample-preparation', href: '/sample-preparation' },
        ],
      },
    ],
  },
  {
    key: 'company',
    label: 'main.company',
    href: '/company',
    description: 'company.description',
    groups: [
      {
        title: 'company.section.story',
        items: [
          { label: 'company.company', href: '/company' },
          { label: 'company.news', href: '/news' },
          {
            label: 'company.notice',
            href: '/notice',
            allowedLocales: ['ko'],
          },
          {
            label: 'company.careers',
            href: 'https://career.3billion.io',
            target: '_blank',
            rel: 'noopener',
            allowedLocales: ['ko'],
          },
        ],
      },
      {
        title: 'company.section.insight',
        items: [{ label: 'company.blog', href: '/blog' }],
      },
    ],
  },
];

/** Site map 하단 + footer 보조 nav. */
export const SITE_NAV_SECONDARY: NavTree = [
  {
    key: 'technology',
    label: 'footer.technology',
    href: '/technology',
    groups: [
      {
        items: [
          { label: 'technology.evidence', href: '/variant-interpretation' },
          { label: 'technology.3cnet', href: '/ai-variant-prediction' },
          { label: 'technology.reanalysis', href: '/reanalysis' },
          { label: 'technology.drug-discovery', href: '/drug-discovery' },
          { label: 'technology.publications', href: '/publications' },
        ],
      },
    ],
  },
  {
    key: 'resources',
    label: 'footer.resources',
    href: '/resources',
  },
  {
    key: 'gene-browser',
    label: 'footer.gene-browser',
    href: '/gene-browser',
  },
  {
    key: 'partnership',
    label: 'footer.partnership',
    href: '/support?type=Partnership#contact',
  },
];

/** Site map 우측 컬럼 — Customer Support / Order (RightNav 와 별도). */
export const SITE_NAV_ACTIONS: NavLink[] = [
  { label: 'etc.customer-support', href: '/support', gtagLabel: 'support' },
  {
    label: 'etc.order',
    href: 'https://portal.3billion.io',
    target: '_blank',
    rel: 'noopener',
    emphasis: true,
    gtagLabel: 'order',
  },
];
